import { CalendarClock, Film, Radio, Video } from 'lucide-react'
import { Link } from 'react-router-dom'
import { DrawerSection } from '@/shared/ui/Drawer'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { useCan } from '@/shared/auth/session'
import { useLoad } from '@/features/administration/hooks'
import { fmtNumber } from '@/shared/lib/format'
import { programCoverage } from '../api'
import { eventStatusLabel, eventTypeLabel } from '@/features/coverage/vocab'
import type { ProgramResponse } from '@/shared/api/types'

/**
 * What was filmed at this programme.
 *
 * The panel is hidden entirely for a caller without `coverage:read` rather than
 * shown empty — an empty panel says "nothing was filmed", which is a claim, and
 * the truthful statement here is "you cannot see this", which does not need a
 * panel to make.
 */
export function ProgramCoveragePanel({ program }: { program: ProgramResponse }) {
  const canSeeCoverage = useCan('coverage:read')
  const { data, error, loading } = useLoad(
    () => (canSeeCoverage ? programCoverage(program.id) : Promise.resolve(null)),
    [program.id, canSeeCoverage],
  )

  if (!canSeeCoverage) return null

  return (
    <DrawerSection title="Coverage">
      <ErrorStrip message={error} />
      {loading && !data ? <LoadingBlock label="Loading coverage…" /> : !data ? null : (
        <div className="space-y-2">
          <div className="flex flex-wrap gap-4 text-erp-sm">
            <Stat icon={<Video className="h-3.5 w-3.5" />} label="Events" value={data.counts.events} />
            <Stat icon={<Radio className="h-3.5 w-3.5" />} label="Live now" value={data.counts.live} accent={data.counts.live > 0} />
            <Stat icon={<Film className="h-3.5 w-3.5" />} label="Recordings" value={data.counts.recordings} />
          </div>

          {data.events.length === 0 ? (
            <Note tone="info">
              No coverage attached to this programme. Create an event on the coverage diary
              and set “Coverage of” to this programme — a launch, a handover, a site visit.
            </Note>
          ) : (
            <ul className="space-y-1">
              {data.events.map((e) => (
                <li key={e.id} className="flex flex-wrap items-center gap-2 rounded-md border border-border p-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate text-erp-sm font-medium">{e.title}</span>
                      {e.live && (
                        <span className="shrink-0 rounded bg-red-600 px-1.5 py-0.5 text-erp-xs font-semibold text-white">
                          LIVE
                        </span>
                      )}
                    </div>
                    <div className="truncate text-erp-xs text-muted-foreground">
                      <span className="font-mono">{e.ref}</span> · {eventTypeLabel(e.eventTypeCode)}
                      {' · '}{eventStatusLabel(e.statusCode)}
                      {e.recordings > 0 && ` · ${fmtNumber(e.recordings)} recording${e.recordings === 1 ? '' : 's'}`}
                    </div>
                  </div>
                  <span className="flex shrink-0 items-center gap-1.5 text-erp-xs text-muted-foreground">
                    <CalendarClock className="h-3 w-3" />
                    {new Date(e.scheduledStart).toLocaleDateString()}
                  </span>
                </li>
              ))}
            </ul>
          )}

          <Link
            className="erp-btn-ghost inline-flex h-7 text-erp-xs text-chocolate"
            to={`/coverage/events?subjectTypeCode=PROGRAM&subjectRef=${program.id}`}
          >
            Open in the coverage diary
          </Link>

          {/*
            The counts come from the same filter as the list, so a badge saying 4
            over a list of 2 cannot happen — the port is tested for exactly that.
            The list is capped at 20 though, and the badge is not.
          */}
          {data.counts.events > data.events.length && (
            <p className="text-erp-xs text-muted-foreground">
              Showing the most recent {data.events.length} of {fmtNumber(data.counts.events)}.
            </p>
          )}
        </div>
      )}
    </DrawerSection>
  )
}

function Stat({
  icon, label, value, accent,
}: { icon: React.ReactNode; label: string; value: number; accent?: boolean }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className={accent ? 'text-red-600' : 'text-muted-foreground'}>{icon}</span>
      <span className="tabular-nums font-semibold">{fmtNumber(value)}</span>
      <span className="text-erp-xs text-muted-foreground">{label}</span>
    </div>
  )
}
